import { ProductType, ValidationRuleId } from '@/lib/graphql/generated/graphql';
import { isDealItemCategoryUpdating } from '@/lib/utils/updating-fields';
import { effectiveValidationErrorsAtom } from '@/states/effective-validation-errors.state';
import { productsMapState, productsState } from '@/states/products.state';
import { hasActiveShopifyConnectionAtom } from '@/states/shopify-connection.state';
import { atom } from 'jotai';

/**
 * A duplicate-variant item is Shopify-recoverable only when it originates from
 * Shopify AND the plugin is currently installed/active (recovery pulls live
 * data from the store). Otherwise it falls back to image / manual fixes.
 */
const isShopifyRecoverable = (
  activeVersion: { shopifyVariantId?: string | null },
  hasActiveShopifyConnection: boolean
): boolean => !!activeVersion.shopifyVariantId && hasActiveShopifyConnection;

/**
 * IDs of selected physical (`object`) deal items that have no product
 * category. Coupons are ignored.
 *
 * Computed client-side (a front-end anomaly, not a backend validation
 * error) so it can power a one-click bulk categorization from the
 * anomalies banner.
 */
export const missingCategoryObjectItemIdsAtom = atom<string[]>((get) => {
  const products = get(productsState);
  return products
    .filter(
      (product) =>
        product.activeVersion.isSelected &&
        product.activeVersion.productType === ProductType.Object &&
        !product.activeVersion.productCategory?.productCategoryId &&
        // Hide items whose categorization is already in progress so the
        // anomaly clears as soon as the fix is triggered.
        !isDealItemCategoryUpdating(product)
    )
    .map((product) => product.id);
});

const LEGACY_REQUIRED_FIELDS_SHIPPING_LABEL = 'Frais de livraison';

/**
 * IDs of selected physical (`object`) deal items flagged as missing their
 * shipping price. Coupons are ignored (defensive: a shipping price must never
 * be bulk-applied to a digital product). Powers the one-click bulk "apply the
 * same shipping price to all" action in the AlertSidebar.
 */
export const missingShippingObjectItemIdsAtom = atom<string[]>((get) => {
  const errorMap = get(effectiveValidationErrorsAtom);
  const productsMap = get(productsMapState);
  const ids: string[] = [];

  for (const [id, errors] of errorMap) {
    if (!errors || errors.length === 0) continue;
    const activeVersion = productsMap.get(id)?.activeVersion;
    if (!activeVersion) continue;
    if (!activeVersion.isSelected) continue;
    if (activeVersion.productType !== ProductType.Object) continue;

    const hasNewShippingError = errors.some(
      (error) => error.ruleId === ValidationRuleId.RequiredShippingFees
    );

    const hasLegacyRequiredFieldsShippingError =
      activeVersion.shippingPrice == null &&
      errors.some(
        (error) =>
          error.ruleId === ValidationRuleId.RequiredFields &&
          error.message.includes(LEGACY_REQUIRED_FIELDS_SHIPPING_LABEL)
      );

    if (hasNewShippingError || hasLegacyRequiredFieldsShippingError) {
      ids.push(id);
    }
  }

  return ids;
});

/**
 * IDs of selected physical (`object`) deal items whose GTIN is invalid
 * (`gtin_validness` error) AND that still carry another identifier — a SKU or a
 * Shopify variant id. Only those can have their invalid GTIN safely cleared:
 * the `sku_or_gtin_or_shopify_variant_id` rule stays satisfied, so removing the
 * GTIN raises no new alert. Items without any other identifier are excluded
 * (clearing their GTIN would trigger that rule) and left for a manual fix.
 * Powers the one-click bulk "remove invalid GTINs" action in the AlertSidebar.
 */
export const invalidGtinObjectItemIdsAtom = atom<string[]>((get) => {
  const errorMap = get(effectiveValidationErrorsAtom);
  const productsMap = get(productsMapState);
  const ids: string[] = [];

  for (const [id, errors] of errorMap) {
    if (!errors || errors.length === 0) continue;
    const activeVersion = productsMap.get(id)?.activeVersion;
    if (!activeVersion) continue;
    if (!activeVersion.isSelected) continue;
    if (activeVersion.productType !== ProductType.Object) continue;

    const hasInvalidGtin = errors.some(
      (error) => error.ruleId === ValidationRuleId.GtinValidness
    );
    if (!hasInvalidGtin) continue;

    const hasOtherIdentifier =
      Boolean(activeVersion.sku) || Boolean(activeVersion.shopifyVariantId);
    if (!hasOtherIdentifier) continue;

    ids.push(id);
  }

  return ids;
});

/**
 * IDs of selected physical (`object`) deal items flagged with the
 * `duplicate_variant` validation error that can be auto-fixed by recovering
 * their variant options from Shopify.
 *
 * Requires BOTH the item to originate from Shopify (`shopifyVariantId` set)
 * AND the Shopify plugin to be currently installed/active
 * (`hasActiveShopifyConnectionAtom`) — recovery hits the live store, so a
 * historical `shopifyVariantId` is not enough. When the plugin is inactive,
 * these items are rerouted to the image / manual fallback buckets below.
 */
export const duplicateVariantShopifyItemIdsAtom = atom<string[]>((get) => {
  const errorMap = get(effectiveValidationErrorsAtom);
  const productsMap = get(productsMapState);
  const hasActiveShopify = get(hasActiveShopifyConnectionAtom);
  const ids: string[] = [];

  for (const [id, errors] of errorMap) {
    if (!errors || errors.length === 0) continue;
    const activeVersion = productsMap.get(id)?.activeVersion;
    if (!activeVersion) continue;
    if (!activeVersion.isSelected) continue;
    if (activeVersion.productType !== ProductType.Object) continue;
    if (!isShopifyRecoverable(activeVersion, hasActiveShopify)) continue;

    const hasDuplicateVariant = errors.some(
      (error) => error.ruleId === ValidationRuleId.DuplicateVariant
    );
    if (hasDuplicateVariant) ids.push(id);
  }

  return ids;
});

/**
 * IDs of selected physical (`object`) deal items flagged with the
 * `duplicate_variant` validation error that the auto-fixes can NOT resolve:
 * not Shopify-recoverable and without images. Coupons are ignored.
 *
 * These "manual" duplicates can only be lifted by editing the variant name
 * or its options by hand, so they drive the "filter + edit in table" path.
 * Shopify-origin items land here when the plugin is inactive and they have
 * no images.
 */
export const duplicateVariantManualItemIdsAtom = atom<string[]>((get) => {
  const errorMap = get(effectiveValidationErrorsAtom);
  const productsMap = get(productsMapState);
  const hasActiveShopify = get(hasActiveShopifyConnectionAtom);
  const ids: string[] = [];

  for (const [id, errors] of errorMap) {
    if (!errors || errors.length === 0) continue;
    const activeVersion = productsMap.get(id)?.activeVersion;
    if (!activeVersion) continue;
    if (!activeVersion.isSelected) continue;
    if (activeVersion.productType !== ProductType.Object) continue;
    if (isShopifyRecoverable(activeVersion, hasActiveShopify)) continue;
    if (activeVersion.images.length > 0) continue;

    const hasDuplicateVariant = errors.some(
      (error) => error.ruleId === ValidationRuleId.DuplicateVariant
    );
    if (hasDuplicateVariant) ids.push(id);
  }

  return ids;
});

/**
 * IDs of selected physical (`object`) deal items flagged with the
 * `duplicate_variant` validation error that are NOT Shopify-recoverable but
 * have images. Coupons are ignored.
 *
 * These are the fallback when no live Shopify source is available: their
 * variant options can be extracted from the product images. Shopify-origin
 * items land here when the plugin is inactive and they have images.
 */
export const duplicateVariantWithImagesItemIdsAtom = atom<string[]>((get) => {
  const errorMap = get(effectiveValidationErrorsAtom);
  const productsMap = get(productsMapState);
  const hasActiveShopify = get(hasActiveShopifyConnectionAtom);
  const ids: string[] = [];

  for (const [id, errors] of errorMap) {
    if (!errors || errors.length === 0) continue;
    const activeVersion = productsMap.get(id)?.activeVersion;
    if (!activeVersion) continue;
    if (!activeVersion.isSelected) continue;
    if (activeVersion.productType !== ProductType.Object) continue;
    if (isShopifyRecoverable(activeVersion, hasActiveShopify)) continue;
    if (activeVersion.images.length === 0) continue;

    const hasDuplicateVariant = errors.some(
      (error) => error.ruleId === ValidationRuleId.DuplicateVariant
    );
    if (hasDuplicateVariant) ids.push(id);
  }

  return ids;
});

/**
 * IDs of selected physical (`object`) Shopify-recoverable deal items flagged
 * with `duplicate_variant` that ALSO have images. Coupons are ignored.
 *
 * These are a subset of `duplicateVariantShopifyItemIdsAtom`: the live Shopify
 * recovery is the primary fix, but when it can't differentiate them (Shopify
 * itself carries the same options) their variant options can still be extracted
 * from the product images. The sidebar offers this image fallback only *after*
 * the user has already attempted the Shopify recovery for the deal (tracked in
 * `shopifyVariantRecoveryAttemptedDealIdsAtom`, session-only).
 */
export const duplicateVariantShopifyWithImagesItemIdsAtom = atom<string[]>(
  (get) => {
    // A strict subset of the Shopify-recoverable duplicates, so derive from that
    // atom and just keep the ones that carry images — no need to re-run the
    // selection / product-type / Shopify-recoverable / error checks.
    const shopifyIds = get(duplicateVariantShopifyItemIdsAtom);
    const productsMap = get(productsMapState);

    return shopifyIds.filter((id) => {
      const activeVersion = productsMap.get(id)?.activeVersion;
      return !!activeVersion && activeVersion.images.length > 0;
    });
  }
);

/**
 * Whether the image-fallback duplicate variants actually originate from Shopify
 * (`shopifyVariantId` set) but can't be recovered live because the plugin is
 * inactive. Distinguishes a "Shopify offer with the plugin off" (worth offering
 * a reconnect) from a genuinely non-Shopify offer, so the AlertSidebar can pick
 * the right description for the duplicate-variant section.
 */
export const hasShopifyOriginImageFallbackDuplicateAtom = atom<boolean>(
  (get) => {
    const errorMap = get(effectiveValidationErrorsAtom);
    const productsMap = get(productsMapState);
    const hasActiveShopify = get(hasActiveShopifyConnectionAtom);

    for (const [id, errors] of errorMap) {
      if (!errors || errors.length === 0) continue;
      const activeVersion = productsMap.get(id)?.activeVersion;
      if (!activeVersion) continue;
      if (!activeVersion.isSelected) continue;
      if (activeVersion.productType !== ProductType.Object) continue;
      if (isShopifyRecoverable(activeVersion, hasActiveShopify)) continue;
      if (activeVersion.images.length === 0) continue;
      if (!activeVersion.shopifyVariantId) continue;

      const hasDuplicateVariant = errors.some(
        (error) => error.ruleId === ValidationRuleId.DuplicateVariant
      );
      if (hasDuplicateVariant) return true;
    }

    return false;
  }
);

/**
 * Whether any duplicate-variant fix (Shopify recovery, image extraction, or
 * manual edit) applies to the current selection. Single source of truth for
 * the anomalies banner badge count and the sidebar section visibility.
 */
export const hasDuplicateVariantFixAtom = atom<boolean>(
  (get) =>
    get(duplicateVariantShopifyItemIdsAtom).length > 0 ||
    get(duplicateVariantWithImagesItemIdsAtom).length > 0 ||
    get(duplicateVariantManualItemIdsAtom).length > 0
);
